import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  TooltipProps,
} from 'recharts';
import { Donut } from 'app/components/Common/Donut/Donut';

export interface IDonutSeriesItem {
  name: string;
  value: number;
}

export interface IDonutInput {
  data: IDonutSeriesItem[];
  labelTitle: string;
  displayValue: string;
  colors: string[];
  labelNoWrap?: boolean;
  hasLegend?: boolean;
  noRounding?: boolean;
}

export interface IBarSeriesItem {
  dataKey: string;
  name: string;
  color: string;
  stackId?: string;
}

export interface ILineSeriesItem {
  dataKey: string;
  name: string;
  color: string;
}

export interface ITooltipKeyConfig {
  key: string;
  label: string;
  format?: 'number' | 'percent';
}

export interface IBarChartInput {
  data: Record<string, unknown>[];
  xAxisKey: string;
  barSeries: IBarSeriesItem[];
  lineSeries?: ILineSeriesItem[];
  tooltipKeys?: ITooltipKeyConfig[];
  onBarClick?: (month: string) => void;
  selectedMonth?: string | null;
}

export interface IDonutAndBarChartProps {
  donut: IDonutInput;
  barChart: IBarChartInput;
  visible?: boolean;
}

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(6),
    backgroundColor: 'white',
    padding: theme.spacing(3, 0),
    width: '100%',
    minHeight: 320,
  },
  donutWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 32%',
    minWidth: 220,
    alignSelf: 'stretch',
  },
  barChartWrapper: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    paddingRight: theme.spacing(2),
  },
  chart: {
    width: '100%',
    height: 280,
  },
}));

const createTooltipContent = (tooltipKeys: ITooltipKeyConfig[]) => {
  const TooltipContent = (props: TooltipProps<number, string>) => {
    const { active, payload, label } = props;
    if (!active || !payload?.length || !label) return null;
    const d = payload[0]?.payload as Record<string, unknown>;
    const formatVal = (key: string, fmt?: 'number' | 'percent') => {
      const v = d?.[key];
      if (v == null) return '0.00';
      return fmt === 'percent' ? Number(v).toFixed(2) : String(Number(v));
    };
    return (
      <div
        style={{
          padding: 8,
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: 4,
          fontSize: 12,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
        {tooltipKeys.map(({ key, label: l, format }) => (
          <div key={key}>
            {l}: {formatVal(key, format)}
          </div>
        ))}
      </div>
    );
  };
  return TooltipContent;
};

const DESELECTED_BAR_FILL = '#E0E0E0';

export const RecruitmentRetentionVisualization1 = ({ donut, barChart, visible = true }: IDonutAndBarChartProps) => {
  const { classes } = useStyles();
  const { barSeries, lineSeries = [], tooltipKeys, xAxisKey, data, onBarClick, selectedMonth } = barChart;

  if (!visible) return null;

  const effectiveTooltipKeys: ITooltipKeyConfig[] =
    tooltipKeys ??
    [
      ...barSeries.map(b => ({ key: b.dataKey, label: b.name } as ITooltipKeyConfig)),
      ...lineSeries.map(l => ({ key: l.dataKey, label: l.name } as ITooltipKeyConfig)),
    ].filter((v, i, a) => a.findIndex(x => x.key === v.key) === i);

  const TooltipContent = createTooltipContent(effectiveTooltipKeys);

  const donutFiltered = donut.data.filter(d => d.value > 0);

  return (
    <Box className={classes.root}>
      <Box className={classes.donutWrapper}>
        <Donut
          data={donutFiltered.length > 0 ? donutFiltered : [{ name: 'placeholder', value: 0 }]}
          colors={donut.colors}
          labelTitle={donut.labelTitle}
          displayValue={donut.displayValue}
          labelNoWrap={donut.labelNoWrap ?? true}
          hasLegend={donut.hasLegend ?? false}
          noRounding={donut.noRounding ?? true}
        />
      </Box>
      <Box className={classes.barChartWrapper}>
        <div className={classes.chart}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }} barCategoryGap="25%">
              <XAxis dataKey={xAxisKey} tick={{ fontSize: 12 }} stroke="#333" />
              <YAxis yAxisId="left" hide allowDecimals={false} domain={[0, 'auto']} />
              <Tooltip content={<TooltipContent />} contentStyle={{ fontSize: 12 }} />
              <Legend verticalAlign="top" align="right" wrapperStyle={{ paddingBottom: 8 }} />
              {barSeries.map(bar => {
                const handleBarClick = (barData: any) => {
                  const payload = barData?.payload ?? barData;
                  const month = payload?.month ?? payload?.[xAxisKey];
                  if (month != null && onBarClick) onBarClick(String(month));
                };
                const getCellFill = (entry: Record<string, unknown>) => {
                  const month = entry?.month ?? entry?.[xAxisKey];
                  const isSelected = !selectedMonth || (month != null && String(month) === selectedMonth);
                  return isSelected ? bar.color : DESELECTED_BAR_FILL;
                };
                return (
                  <Bar
                    key={bar.dataKey}
                    yAxisId="left"
                    dataKey={bar.dataKey}
                    stackId={bar.stackId ?? 'a'}
                    fill={bar.color}
                    name={bar.name}
                    onClick={onBarClick ? handleBarClick : undefined}
                    cursor={onBarClick ? 'pointer' : undefined}
                  >
                    {data.map((entry, idx) => (
                      <Cell key={idx} fill={getCellFill(entry as Record<string, unknown>)} />
                    ))}
                  </Bar>
                );
              })}
              {lineSeries.map(line => (
                <Line
                  key={line.dataKey}
                  yAxisId="left"
                  type="monotone"
                  dataKey={line.dataKey}
                  name={line.name}
                  stroke={line.color}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  legendType="line"
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Box>
    </Box>
  );
};
